import { Injectable } from '@angular/core';
import { AnalyticsConfigService } from '@core/services/core/analytics/analytics-configuration';
import { getDOMPath } from 'src/app/core/utils/utils';
import { AnalyticsContext } from './analytics-context';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsClicks {
  constructor(
    private analyticsContext: AnalyticsContext,
    private analyticsConfigService: AnalyticsConfigService
  ) {}

  get labelOverride() {
    return this.analyticsConfigService.getConfig().labelOverride;
  }
  get action() {
    return this.analyticsConfigService.getConfig().action;
  }
  get category() {
    return this.analyticsConfigService.getConfig().category;
  }
  get excludeTags() {
    return this.analyticsConfigService.getConfig().excludeTags;
  }

  async getClickParams(ev: any) {
    const { actionNode, wrapperNode } = this.scrapeNodes(ev) || ({} as any);
    const {
      analyticsCategory,
      analyticsDisable,
      analyticsLabel,
      analyticsAction
    } = (actionNode && actionNode.attributes) || ({} as any);
    const analyticsCategoryFromWrapper =
      wrapperNode &&
      wrapperNode.attributes &&
      wrapperNode.attributes.analyticsCategory;
    if (!actionNode || analyticsDisable) {
      return Promise.resolve({ disabled: true });
    }

    let category =
      (analyticsCategory && analyticsCategory.value) ||
      (analyticsCategoryFromWrapper && analyticsCategoryFromWrapper.value) ||
      (wrapperNode && wrapperNode.nodeName) ||
      this.category.get('DEFAULT');
    if (!analyticsCategory) {
      category = this.category.get(category) && this.category.get(category).toLowerCase();
    }
    let action =
      (analyticsAction && analyticsAction.value) ||
      (actionNode && actionNode.nodeName);
    if (!analyticsAction) {
      action = 'click ' + (this.action.get(action) && this.action.get(action).toLowerCase());
    }
    const label = (
      (analyticsLabel && analyticsLabel.value) ||
      this.labelOverride.get(actionNode.nodeName) ||
      this.scrapeLabel(actionNode)
    )
      .substring(0, 99)
      .toLowerCase();
    const {
      mandatoryFields,
      optionalFields
    } = await this.analyticsContext.getFields();
    if (mandatoryFields.CD37_url?.includes('/payment-plan')) {
      category = 'page button';
    }
    return Promise.resolve({
      category,
      action,
      label,
      ...mandatoryFields,
      ...optionalFields
    });
  }

  async getCustomClickParams(category?: string, action?: string, label?: string) {
    const { mandatoryFields, optionalFields } = await this.analyticsContext.getFields();
    return {
      category: category && category.toLowerCase(),
      action: action && action.toLowerCase(),
      label: label && label.toLowerCase(),
      ...mandatoryFields,
      ...optionalFields
    };
  }

  private scrapeNodes(ev: any) {
    const path = ev.path || getDOMPath(ev);
    let actionNode;
    let wrapperNode;
    let analyticsActionFound = false;
    for (const node of path) {
      const { analyticsAction } = node.attributes || ({} as any);
      if (analyticsAction) {
        actionNode = node;
        analyticsActionFound = true;
        continue;
      }

      if (
        !analyticsActionFound &&
        Array.from(this.action.keys()).includes(node.nodeName)
      ) {
        actionNode = node;
      } else if (
        !analyticsActionFound &&
        this.excludeTags.includes(node.nodeName)
      ) {
        actionNode = undefined;
        wrapperNode = undefined;
        break;
      } else if (Array.from(this.category.keys()).includes(node.nodeName)) {
        wrapperNode = node;
        break;
      }
    }

    return { actionNode, wrapperNode };
  }

  private scrapeLabel(node) {
    let res = node.innerText;
    if (res !== '') return res;

    // Check if it´s an image or has an image in one of its children
    const imgNode: any = [
      node,
      ...Array.from(node.children)
    ].find((childrenNode: any) =>
      ['IMG', 'ION-ICON'].includes(childrenNode.nodeName)
    );

    const imgId =
      imgNode &&
      imgNode.attributes &&
      imgNode.attributes.id &&
      imgNode.attributes.id.value;
    const imgAlt =
      imgNode &&
      imgNode.attributes &&
      imgNode.attributes.alt &&
      imgNode.attributes.alt.value;
    const iconName =
      imgNode &&
      imgNode.attributes &&
      imgNode.attributes.name &&
      imgNode.attributes.name.value;

    res = imgAlt || imgId || iconName;

    return res || '';
  }
}
